import React, { Dispatch, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store, { IAppReduxStore } from '../../../redux/store';
import {
    Autocomplete,
    Button,
    CircularProgress,
    SelectChangeEvent,
    Stack,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import { headingFontBold, vars } from '../../../Theme';
import {
    IDocTypeConventionsSection,
    IDocTypeConventionsSectionValue,
    IResultDocNumberObjValue,
    saveDocNumber,
    toggleDocnumberSidepanel,
} from '../../../redux/import/sliceDocnumberSidepanel';
import { fullSize } from '../../../Constants';
import { useTranslation } from 'react-i18next';
import SidepanelDropdown from './Components/SidepanelDropdown';
import { useParams } from 'react-router-dom';
import { checkTextFieldCharacters, checkTextFieldLength } from '../../../util/DataUtils';
import SidepanelHeader from './SidepanelHeader';
import SidepanelWrapper from './SidepanelWrapper';

/** Props for the typography. */
const typographySx: SxProps = {
    fontSize: '12px',
    color: vars.concrete,
};

/** Props for the button. */
const buttonSx: SxProps = {
    height: '36px',
    ml: 1,
    textTransform: 'capitalize',
    fontSize: '14px',
    color: vars.font,
    ...headingFontBold,
};

/** Sidepanel that allows to change document number */
export default ({ onSaved }: { onSaved?: () => void }) => {
    const { replicaId, areaName } = useParams();

    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'sidepanel' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();
    const previousDocNumberStr = useRef<string>(null);
    const [docTypeConvention, setDocTypeConvention] = useState<string>('');
    const [docNumberObj, setDocNumberObj] = useState<IDocNumberObj>(null);
    const [resultDocNumberObj, setResultDocNumberObj] = useState<IResultDocNumberObjValue>(null);
    const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState<boolean>(true);
    const [hasErrorObj, setHasErrorObj] = useState<Record<string, string>>(null);

    const {
        data: sidepanelData,
        loading: sidepanelDataLoading,
        error: sidepanelDataError,
        isSidepanelOpen,
        isIndexFieldDisabled,
        loadingSaveDocNumber,
        errorSaveDocNumber,
    } = useSelector((_store: IAppReduxStore) => _store.docnumberSidepanel);

    /** State that shows if the sidepanel is expanded. */
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const createDocNumberStr = useMemo(() => {
        if (!docNumberObj) return;
        let resultString = '';
        let prevSeparator = '';
        let resObj = null;
        for (const ob in docNumberObj) {
            if (docNumberObj[ob].fieldName) {
                resObj = {
                    ...(resObj ?? {}),
                    [docNumberObj[ob].fieldName]: docNumberObj[ob].value,
                    DocNumber:
                        docNumberObj[ob].fieldName === 'DocIndex' && prevSeparator
                            ? resultString.slice(0, -1)
                            : resultString,
                };
            }
            resultString = resultString + docNumberObj[ob].value + docNumberObj[ob].separator;
            prevSeparator = docNumberObj[ob].separator;
        }

        if (!previousDocNumberStr.current) {
            previousDocNumberStr.current = resultString;
        }

        setResultDocNumberObj(resObj);
        return resultString;
    }, [docNumberObj]);

    const clearState = () => {
        setDocNumberObj(null);
        setDocTypeConvention('');
        setHasErrorObj(null);
        previousDocNumberStr.current = null;
    };

    /* Handles expanding/collapsing the sidepanel */
    const handleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const sectionsList = useMemo(() => {
        return (
            sidepanelData?.DocTypeConventions?.find(item => item.key === docTypeConvention)
                ?.sections ?? null
        );
    }, [docTypeConvention, sidepanelData]);

    useEffect(() => {
        if (!sidepanelData) return;
        setDocTypeConvention(
            sidepanelData?.DocTypeConventions?.find(item => item.selected)?.key ??
                sidepanelData?.DocTypeConventions?.[0]?.key ??
                '',
        );
    }, [sidepanelData]);

    useEffect(() => {
        if (!sectionsList) return;
        setDocNumberObj(
            sectionsList.reduce((obj, section) => {
                obj[section.displayName] = {
                    separator: section.seperator,
                    value:
                        section.displayType === 'dropdown' && !section.value
                            ? section.values[0].key
                            : section.value,
                    fieldName: section.fieldName,
                };
                return obj;
            }, {}),
        );
    }, [sectionsList]);

    useEffect(() => {
        if (
            previousDocNumberStr.current === createDocNumberStr ||
            hasErrorObj !== null ||
            !requiredFieldsAreSet
        ) {
            setIsSaveBtnDisabled(true);
            return;
        }

        setIsSaveBtnDisabled(false);
    }, [createDocNumberStr]);

    useEffect(() => {
        if (isSidepanelOpen) return;
        clearState();
    }, [isSidepanelOpen]);

    const handleChangeDocTypeConvention = (event: SelectChangeEvent) => {
        setDocTypeConvention(event.target.value);
    };

    const handleChangeDocTypeConventionsSectionValue = ({
        event,
        newValue,
        section,
        defaultValue,
    }: {
        section: IDocTypeConventionsSection;
        event?: React.ChangeEvent<HTMLInputElement> | null;
        newValue?: IDocTypeConventionsSectionValue | null;
        defaultValue?: string | null;
    }) => {
        const objKey = section.displayType === 'dropdown' ? section.displayName : event.target.name;
        const objValue =
            section.displayType === 'dropdown' ? newValue?.key ?? defaultValue : event.target.value;
        setDocNumberObj(prev => {
            return {
                ...prev,
                [objKey]: {
                    ...prev[objKey],
                    value: objValue,
                },
            };
        });
        if (section.displayType === 'textinput') {
            const checkTextFieldLengthError = checkTextFieldLength({
                fieldValue: objValue,
                from: section.minLength ?? 0,
                to: section.maxLength ?? Infinity,
                optional: section.optional,
            });
            const checkTextFieldCharactersError = checkTextFieldCharacters(
                objValue,
                section.prohibitedCharacters,
            );
            if (!checkTextFieldLengthError && !checkTextFieldCharactersError) {
                return setHasErrorObj(null);
            } else {
                setHasErrorObj(prev => {
                    return {
                        ...prev,
                        [objKey]: checkTextFieldLengthError || checkTextFieldCharactersError,
                    };
                });
            }
        }
    };

    /** Wether all required fields are set. */
    const requiredFieldsAreSet = useMemo((): boolean => {
        if (!docNumberObj) return false;

        for (const field in docNumberObj) {
            const fieldValue = docNumberObj[field];
            const fieldRule = (sectionsList ?? []).find(x => x.displayName === field);

            if (fieldRule && !fieldRule.optional && !fieldValue.value) return false; // some required field is not set
        }

        return true;
    }, [docNumberObj, sectionsList]);

    useEffect(() => {
        return () => {
            handleCancelSidepanel();
        };
    }, [replicaId, areaName]);

    const handleCancelSidepanel = () => {
        dispatch(toggleDocnumberSidepanel(false));
    };

    const handleSaveDocNumber = async () => {
        await store.dispatch(
            saveDocNumber({
                replicaId,
                universalId: sidepanelData.universialId,
                updatedDocNumer: resultDocNumberObj,
            }),
        );
        if (errorSaveDocNumber) return;
        onSaved?.();
        dispatch(toggleDocnumberSidepanel(false));
        previousDocNumberStr.current = null;
    };

    /** Waiting on a request, so show a loading indicator. */
    if ((sidepanelDataError || errorSaveDocNumber) && isSidepanelOpen) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...fullSize,
                    px: 2,
                }}
            >
                <Typography>{t('docnumberSidepanelError')}</Typography>
            </Stack>
        );
    }

    /** Waiting on a request, so show a loading indicator. */
    if (sidepanelDataLoading) {
        return (
            <Stack
                sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...fullSize,
                    minWidth: '350px',
                }}
            >
                <CircularProgress />
            </Stack>
        );
    }

    return isSidepanelOpen ? (
        <SidepanelWrapper isExpanded={isExpanded} addDivider={false}>
            <SidepanelHeader
                sx={{
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${vars.lightGray}`,
                    mb: 2,
                }}
                type={sidepanelData?.docType}
                firtsRowTitle={createDocNumberStr}
                secondRowTitle={sidepanelData?.subject}
                handleClose={handleCancelSidepanel}
                handleExpand={handleExpand}
                isExpanded={isExpanded}
            />
            <Stack sx={{ px: 2, pb: 2, borderBottom: `1px solid ${vars.lightGray}` }}>
                <SidepanelDropdown
                    options={sidepanelData?.DocTypeConventions ?? []}
                    value={docTypeConvention}
                    handleChange={handleChangeDocTypeConvention}
                />
            </Stack>
            <Stack
                sx={{
                    flexGrow: '1',
                    overflow: 'auto',
                    pt: 2,
                    px: 2,
                }}
            >
                {(sectionsList ?? []).map((section, i) => {
                    if (section.displayType === 'dropdown') {
                        return (
                            <Autocomplete
                                // 7683: disable index field for admin action "editDocumentNumberAdmin"
                                disabled={section.fieldName === 'DocIndex' && isIndexFieldDisabled}
                                size='small'
                                key={`${section.displayName}_${i}`}
                                sx={{
                                    mb: 2,
                                    '& .MuiInputBase-input': {
                                        fontSize: '14px',
                                    },
                                }}
                                value={
                                    section.values.find(
                                        item =>
                                            item.key === docNumberObj?.[section.displayName]?.value,
                                    ) ?? section.values[0]
                                }
                                onChange={(_, newValue: IDocTypeConventionsSectionValue | null) => {
                                    handleChangeDocTypeConventionsSectionValue({
                                        section,
                                        newValue,
                                        defaultValue: section.values?.[0].key,
                                    });
                                }}
                                options={section.values}
                                renderOption={(props, option) => (
                                    <Typography
                                        key={`${option.key}_${option.displayValue}`}
                                        sx={{ fontSize: '14px' }}
                                        {...props}
                                    >
                                        {option.displayValue}
                                    </Typography>
                                )}
                                getOptionLabel={(option: IDocTypeConventionsSectionValue) =>
                                    option.displayValue
                                }
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={section.displayName}
                                        sx={{ fontSize: '14px' }}
                                    />
                                )}
                            />
                        );
                    }
                    return (
                        <TextField
                            required={!section.optional}
                            error={!!hasErrorObj?.[section.displayName]}
                            key={`${section.displayName}_${i}`}
                            sx={{
                                mb: 2,
                                '& .MuiFormLabel-root': {
                                    fontSize: '14px',
                                },
                            }}
                            name={section.displayName}
                            label={section.displayName}
                            value={docNumberObj?.[section.displayName]?.value ?? section.value}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                handleChangeDocTypeConventionsSectionValue({
                                    event,
                                    section,
                                });
                            }}
                            helperText={
                                hasErrorObj?.[section.displayName]
                                    ? t(`${hasErrorObj?.[section.displayName]}`, {
                                          from: section.minLength,
                                          to: section.maxLength,
                                      })
                                    : ''
                            }
                            size='small'
                        />
                    );
                })}
            </Stack>
            <Stack sx={{ mb: 2, px: 2 }}>
                <Typography sx={typographySx}>{t('fileNameForUpload')}</Typography>
                <Typography sx={typographySx}>{sidepanelData?.filename ?? ''}</Typography>
            </Stack>
            <Stack
                direction='row'
                sx={{
                    justifyContent: 'flex-end',
                    borderTop: `1px solid ${vars.lightGray}`,
                    p: 2,
                }}
            >
                <Button
                    sx={{
                        ...buttonSx,
                        '&:hover': {
                            backgroundColor: 'transparent',
                            color: vars.black,
                        },
                    }}
                    onClick={handleCancelSidepanel}
                >
                    {t('cancel')}
                </Button>
                <Button
                    sx={{
                        height: '36px',
                        ml: 1,
                        textTransform: 'capitalize',
                        fontSize: '14px',
                        ...headingFontBold,
                        color: vars.white,
                    }}
                    onClick={handleSaveDocNumber}
                    variant='contained'
                    loading={loadingSaveDocNumber}
                    disabled={isSaveBtnDisabled ?? true}
                >
                    {t('save')}
                </Button>
            </Stack>
        </SidepanelWrapper>
    ) : (
        <></>
    );
};

/**
 * Interface that describes the value of the docNumberObj.
 */
export interface IDocNumberObjValue {
    separator: string;
    value: string;
    fieldName: string;
}

/**
 * Interface that describes the docNumberObj.
 */
export type IDocNumberObj = Record<string, IDocNumberObjValue>;
